/**
 * Client-side error reporting: ships an uncaught frontend error to the backend so it lands in the
 * server log (CloudWatch / SST). The browser console only exists on the user's machine — without this,
 * a production crash is invisible. Wired from three places (all in `src/router.tsx` /
 * `src/route-error.tsx`): the router-wide error boundary, the global `error` handler, and the global
 * `unhandledrejection` handler.
 */

/**
 * Normalize an arbitrary thrown value into `{ name, message, stack }`. A thrown value can be anything
 * (an Error, a string, null, a tRPC error object, …), so this never assumes `instanceof Error`.
 * Pure and dependency-free so it's unit-testable in isolation.
 */
export function extractErrorInfo(error: unknown): { name: string; message: string; stack: string | undefined } {
  if (error instanceof Error) {
    return { name: error.name || 'Error', message: error.message || '(empty message)', stack: error.stack }
  }
  if (typeof error === 'string') {
    return { name: 'StringError', message: error, stack: undefined }
  }
  if (error == null) {
    return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined }
  }
  if (typeof error === 'object') {
    const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError'
    const message =
      'message' in error && typeof error.message === 'string'
        ? error.message
        : 'error' in error && typeof error.error === 'string'
          ? error.error
          : safeStringify(error)
    const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined
    return { name, message, stack }
  }
  return { name: 'UnknownError', message: safeStringify(error), stack: undefined }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/**
 * Is this a tRPC *client* error (a `TRPCClientError`)? Those are controlled server responses — a known
 * machine `ErrorCode`, a 401, an input-validation failure, even a redacted 500 — that the client already
 * handles in `toastErrorLink` (toast/redirect), and whose server-side throw is already logged where it
 * happened. They are NOT frontend JS exceptions, which is the only thing this reporter exists to capture,
 * so they must never be forwarded to the backend log.
 *
 * Duck-typed rather than `instanceof TRPCClientError`: bundling can give the thrown error a different
 * class identity across module boundaries (same reason `route-error.tsx` duck-types), but
 * `TRPCClientError` always sets `name = 'TRPCClientError'` in its constructor, and carries a `data`
 * payload with the transport `code` — either is a reliable signal.
 */
export function isTrpcClientError(error: unknown): boolean {
  if (typeof error !== 'object' || error == null) return false
  const e = error as { name?: unknown; data?: { code?: unknown } }
  if (e.name === 'TRPCClientError') return true
  return typeof e.data === 'object' && e.data != null && typeof e.data.code === 'string'
}

/**
 * Send an error to the backend (persisted + deduped as an `errors` row, and — when enabled — a
 * `client_error` wide event). Fire-and-forget: never throws and never awaits the caller. `source` tags
 * where it was caught (`boundary` | `global` | `unhandledrejection`). The recent nav/click/console
 * breadcrumb trail is attached so a crash is reproducible; correlation ids (trace/session) ride on the
 * tRPC request headers (see main.tsx). Everything else (identity, browser) is derived server-side.
 */
export function logFrontendError(
  error: unknown,
  source: 'boundary' | 'global' | 'unhandledrejection' = 'boundary',
): void {
  // Server-side throws are already logged where they happen (trpc.ts / loaders); this path is for the
  // browser only, and `trpcClient` uses window-coupled HTTP links that don't exist on the server.
  if (typeof window === 'undefined') return
  // tRPC client errors are controlled, expected responses (already handled by toastErrorLink and logged
  // server-side), not frontend exceptions — don't ship them to the backend log. See isTrpcClientError.
  if (isTrpcClientError(error)) return
  const info = extractErrorInfo(error)
  // Import lazily to keep the window-coupled `@/main` singletons (and breadcrumbs) out of anything that
  // only wants the pure `extractErrorInfo` (e.g. its unit test).
  Promise.all([import('../main'), import('./breadcrumbs')])
    .then(([{ trpcClient }, { getBreadcrumbs }]) =>
      trpcClient.common.logFrontendError.mutate({
        route: window.location.pathname,
        name: info.name,
        message: info.message,
        stack: info.stack,
        source,
        breadcrumbs: getBreadcrumbs(),
      }),
    )
    .catch(() => {
      // Swallow — reporting the error must never itself surface an error.
    })
}
